import { useEffect, useState } from "react";
import { Star, X } from "lucide-react";
import {
  GOOGLE_RATING,
  GOOGLE_REVIEW_COUNT,
  snapshotReviews,
} from "@/data/reviews-snapshot";

const REVIEWS = [
  ...snapshotReviews,
  {
    name: "Bonnie Delaney",
    when: "recently",
    text: "Capital Pro is a wonderful company! Luis and Miller soft washed the exterior and it looks like a brand new house!",
  },
  {
    name: "Kathy Valason",
    when: "recently",
    text: "Thank you Capital Pro for doing such a phenomenal job power washing my house. It's like I have a new house!",
  },
  {
    name: "Rosemary Trusso",
    when: "recently",
    text: "Excellent job cleaning and sealing our stamped concrete patio. Professional and pleasant to deal with.",
  },
].map((r, i) => ({
  id: `r${i}`,
  authorName: r.name,
  rating: 5,
  text: r.text,
  relativeTime: r.when,
}));

const REVIEWS_URL =
  "https://www.google.com/search?q=Capital+Pro+Pressure+Washing+Albany+NY#lrd=0x63da28b8a2ca697d:0xccd7a00f5742eca1,1";

/** Floating Google review notification that rotates through live reviews. */
export function GoogleReviewPopup({ delayMs = 4000 }: { delayMs?: number }) {
  const [visible, setVisible] = useState(false);
  const [closed, setClosed] = useState(false);
  const [index, setIndex] = useState(0);

  const reviews = REVIEWS;
  const rating = GOOGLE_RATING;
  const count = GOOGLE_REVIEW_COUNT;

  useEffect(() => {
    const t = window.setTimeout(() => setVisible(true), delayMs);
    return () => window.clearTimeout(t);
  }, [delayMs]);

  useEffect(() => {
    if (!visible || closed) return;
    const rotate = window.setInterval(() => {
      setVisible(false);
      window.setTimeout(() => {
        setIndex((i) => (i + 1) % reviews.length);
        setVisible(true);
      }, 600);
    }, 9000);
    return () => window.clearInterval(rotate);
  }, [visible, closed, reviews.length]);

  if (closed) return null;
  const review = reviews[index % reviews.length];
  if (!review) return null;

  const text =
    review.text.length > 150 ? `${review.text.slice(0, 147).trimEnd()}…` : review.text;

  return (
    <aside
      className={`grp-pop${visible ? " grp-pop-in" : ""}`}
      aria-live="polite"
      aria-label="Recent Google review"
    >
      <button
        type="button"
        className="grp-close"
        aria-label="Dismiss review"
        onClick={() => setClosed(true)}
      >
        <X size={14} aria-hidden="true" />
      </button>

      <a className="grp-link" href={REVIEWS_URL} target="_blank" rel="noopener noreferrer">
        <div className="grp-head">
          <span className="grp-avatar" aria-hidden="true">
            {review.authorName.charAt(0)}
          </span>
          <div className="grp-head-copy">
            <span className="grp-name">{review.authorName}</span>
            <span className="grp-meta">
              <svg viewBox="0 0 24 24" width="12" height="12" aria-hidden="true">
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1Z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.65l-3.57-2.77c-.99.66-2.26 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.11a6.6 6.6 0 0 1 0-4.22V7.05H2.18a11 11 0 0 0 0 9.9l3.66-2.84Z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.96 1 12 1a11 11 0 0 0-9.82 6.05l3.66 2.84C6.71 7.3 9.14 5.38 12 5.38Z"
                />
              </svg>
              Google review
              {review.relativeTime ? ` · ${review.relativeTime}` : ""}
            </span>
          </div>
        </div>

        <div className="grp-stars" aria-label={`${review.rating} out of 5 stars`}>
          {Array.from({ length: 5 }).map((_, i) => (
            <Star
              key={i}
              size={13}
              aria-hidden="true"
              fill={i < Math.round(review.rating) ? "#FBBC05" : "none"}
              stroke="#FBBC05"
            />
          ))}
        </div>

        <p className="grp-text">“{text}”</p>

        <span className="grp-foot">
          {rating.toFixed(1)}★{count ? ` from ${count} Google reviews` : " on Google"}
        </span>
      </a>
    </aside>
  );
}
